// src/components/reports/ActionForm.tsx
import { useState } from "react";
import { useReportsStore } from "@/store/reportsStore";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import { Trash2, Plus } from "lucide-react";

interface YearEntry {
  year: string;
  beneficiaries: string;
  volunteers: string;
  ro: string;              // ← новое
  layingPoints: string;    // ← новое
}

interface ActionFormProps {
  onClose: () => void;
  existingActionId?: string;
  existingActionName?: string;
}

export default function ActionForm({
  onClose,
  existingActionId,
  existingActionName,
}: ActionFormProps) {
  const { addAction, addYearToAction } = useReportsStore();

  const isEditMode = !!existingActionId;

  const [actionName, setActionName] = useState(existingActionName || "");
  const [years, setYears] = useState<YearEntry[]>([
    { year: "", beneficiaries: "", volunteers: "", ro: "", layingPoints: "" },
  ]);

  const [isSaving, setIsSaving] = useState(false);

  const addYearBlock = () => {
    setYears([...years, { year: "", beneficiaries: "", volunteers: "", ro: "", layingPoints: "" }]);
  };

  const removeYearBlock = (index: number) => {
    if (years.length === 1) {
      toast({ title: "Нельзя удалить последний год", variant: "destructive" });
      return;
    }
    setYears(years.filter((_, i) => i !== index));
  };

  const updateYearField = (index: number, field: keyof YearEntry, value: string) => {
    const newYears = [...years];
    newYears[index] = { ...newYears[index], [field]: value };
    setYears(newYears);
  };

  const handleSubmit = async () => {
    // Валидация (только обязательные поля)
    if (!actionName.trim() && !isEditMode) {
      toast({ title: "Укажите название акции", variant: "destructive" });
      return;
    }

    for (const y of years) {
      const yr = Number(y.year);
      if (!y.year || isNaN(yr) || yr < 1900 || yr > 2100) {
        toast({ title: "Некорректный год", variant: "destructive" });
        return;
      }
      if (!y.beneficiaries || isNaN(Number(y.beneficiaries)) || Number(y.beneficiaries) < 0) {
        toast({ title: "Укажите благополучателей", variant: "destructive" });
        return;
      }
      if (!y.volunteers || isNaN(Number(y.volunteers)) || Number(y.volunteers) < 0) {
        toast({ title: "Укажите добровольцев", variant: "destructive" });
        return;
      }
      // РО и Точки возложения — НЕ валидируем (необязательные)
    }

    setIsSaving(true);

    try {
      let actionId = existingActionId;

      if (!isEditMode) {
        console.log("[ActionForm] Создаём новую акцию:", actionName.trim());
        actionId = await addAction(actionName.trim());
        console.log("[ActionForm] Получен ID новой акции:", actionId);
      } else {
        console.log("[ActionForm] Добавляем год к существующей акции ID:", actionId);
      }

      for (const y of years) {
        const yearData: any = {
          year: Number(y.year),
          beneficiaries: Number(y.beneficiaries),
          volunteers: Number(y.volunteers),
        };

        // Добавляем новые поля только если они заполнены
        if (y.ro && !isNaN(Number(y.ro)) && Number(y.ro) >= 0) {
          yearData.ro = Number(y.ro);
        }
        if (y.layingPoints && !isNaN(Number(y.layingPoints)) && Number(y.layingPoints) >= 0) {
          yearData.layingPoints = Number(y.layingPoints);
        }

        console.log("[ActionForm] Добавляем год:", yearData);
        await addYearToAction(actionId!, yearData);
      }

      toast({
        title: "Успех",
        description: isEditMode
          ? `Добавлен год к акции "${actionName}"`
          : `Акция "${actionName}" сохранена`,
      });

      onClose();
    } catch (error: any) {
      console.error("[ActionForm] ОШИБКА СОХРАНЕНИЯ:", error);
      toast({
        title: "Ошибка сохранения",
        description: error.message || "Проверьте консоль и правила Firestore",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-8 py-4 max-h-[70vh] overflow-y-auto pr-2">
      {/* Название акции */}
      {!isEditMode && (
        <div className="pb-6 border-b border-purple-700/50">
          <Label className="text-purple-200 mb-3 block text-xl font-semibold">
            Наименование акции
          </Label>
          <Input
            placeholder="Например: Десант"
            value={actionName}
            onChange={(e) => setActionName(e.target.value)}
            className="bg-purple-950 border-purple-600 text-white placeholder:text-purple-500 text-lg py-6 focus:ring-purple-500"
            disabled={isSaving}
          />
        </div>
      )}

      {/* Блоки годов */}
      <div className="space-y-8">
        {years.map((entry, index) => (
          <div
            key={index}
            className="p-6 bg-purple-950/40 border border-purple-700/60 rounded-xl relative shadow-md"
          >
            <div className="absolute -top-3 left-6 bg-purple-950 px-4 py-1 rounded-full text-sm font-medium text-purple-300 border border-purple-600/50">
              Год {index + 1}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mt-4">
              {/* Год */}
              <div>
                <Label className="text-purple-200 mb-2 block text-base font-medium">Год</Label>
                <Input
                  type="number"
                  placeholder="2025"
                  value={entry.year}
                  onChange={(e) => updateYearField(index, "year", e.target.value)}
                  className="bg-purple-900/70 border-purple-600 text-white placeholder:text-purple-500 py-5 text-base"
                  disabled={isSaving}
                />
              </div>

              {/* Благополучатели */}
              <div>
                <Label className="text-purple-200 mb-2 block text-base font-medium">Благополучатели</Label>
                <Input
                  type="number"
                  placeholder="10000"
                  value={entry.beneficiaries}
                  onChange={(e) => updateYearField(index, "beneficiaries", e.target.value)}
                  className="bg-purple-900/70 border-purple-600 text-white placeholder:text-purple-500 py-5 text-base"
                  disabled={isSaving}
                />
              </div>

              {/* Добровольцы */}
              <div>
                <Label className="text-purple-200 mb-2 block text-base font-medium">Добровольцы</Label>
                <Input
                  type="number"
                  placeholder="400"
                  value={entry.volunteers}
                  onChange={(e) => updateYearField(index, "volunteers", e.target.value)}
                  className="bg-purple-900/70 border-purple-600 text-white placeholder:text-purple-500 py-5 text-base"
                  disabled={isSaving}
                />
              </div>

              {/* РО */}
              <div>
                <Label className="text-purple-200 mb-2 block text-base font-medium">РО</Label>
                <Input
                  type="number"
                  placeholder="0"
                  value={entry.ro}
                  onChange={(e) => updateYearField(index, "ro", e.target.value)}
                  className="bg-purple-900/70 border-purple-600 text-white placeholder:text-purple-500 py-5 text-base"
                  disabled={isSaving}
                />
              </div>

              {/* Точки возложения */}
              <div>
                <Label className="text-purple-200 mb-2 block text-base font-medium">Точки возложения</Label>
                <Input
                  type="number"
                  placeholder="0"
                  value={entry.layingPoints}
                  onChange={(e) => updateYearField(index, "layingPoints", e.target.value)}
                  className="bg-purple-900/70 border-purple-600 text-white placeholder:text-purple-500 py-5 text-base"
                  disabled={isSaving}
                />
              </div>
            </div>

            {years.length > 1 && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => removeYearBlock(index)}
                className="absolute top-4 right-4 text-red-400 hover:text-red-300 hover:bg-red-900/30 rounded-full p-2"
                disabled={isSaving}
              >
                <Trash2 className="h-5 w-5" />
              </Button>
            )}
          </div>
        ))}
      </div>

      <div className="flex justify-center pt-4">
        <Button
          variant="outline"
          onClick={addYearBlock}
          disabled={isSaving}
          className="border-purple-600 text-purple-200 hover:bg-purple-900/50 px-10 py-6 text-lg flex items-center gap-2"
        >
          <Plus className="h-5 w-5" />
          Добавить ещё год
        </Button>
      </div>

      <div className="flex justify-end gap-4 mt-10 pt-6 border-t border-purple-700/50">
        <Button
          variant="outline"
          onClick={onClose}
          disabled={isSaving}
          className="border-purple-600 text-purple-200 hover:bg-purple-900/50 px-10 py-6 text-lg"
        >
          Отмена
        </Button>

        <Button
          onClick={handleSubmit}
          disabled={isSaving}
          className="bg-gradient-to-r from-purple-600 to-purple-800 hover:from-purple-700 hover:to-purple-900 px-12 py-6 text-lg min-w-[200px] shadow-lg"
        >
          {isSaving ? (
            <span className="flex items-center gap-2">
              <svg className="animate-spin h-5 w-5 text-white" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              </svg>
              Сохранение...
            </span>
          ) : isEditMode ? "Добавить год" : "Сохранить всё"}
        </Button>
      </div>
    </div>
  );
}